import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import NotificationBell from './NotificationBell';
import { supabase } from '../supabase/config';
import logoSvg from '../assets/logo.svg';

// MUI components
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Container
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Home as HomeIcon,
  People as PeopleIcon,
  Login as LoginIcon
} from '@mui/icons-material';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  
  // Update display name and avatar when user changes
  useEffect(() => {
    if (user) {
      setDisplayName(user.user_metadata?.display_name || user.email || 'User');
      
      // Fetch user profile data including avatar
      const fetchUserProfile = async () => {
        try {
          // First check if we have avatar_url in user metadata
          if (user.user_metadata?.avatar_url) {
            const { data } = await supabase
              .storage
              .from('avatars')
              .getPublicUrl(user.user_metadata.avatar_url);
            
            if (data && data.publicUrl) {
              setAvatarUrl(data.publicUrl);
              return;
            }
          }
          
          // If not in metadata, check the users table
          const { data: userData, error } = await supabase
            .from('users')
            .select('avatar_url')
            .eq('id', user.id)
            .single();
          
          if (!error && userData && userData.avatar_url) {
            const { data } = await supabase
              .storage
              .from('avatars')
              .getPublicUrl(userData.avatar_url);
            
            if (data && data.publicUrl) {
              setAvatarUrl(data.publicUrl);
            }
          }
        } catch (err) {
          console.error('Error fetching user avatar:', err);
        }
      };
      
      fetchUserProfile();
    }
  }, [user]);
  
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleMenuClose();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const menuId = 'primary-account-menu';
  const isMenuOpen = Boolean(anchorEl);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      id={menuId}
      keepMounted
      open={isMenuOpen}
      onClose={handleMenuClose}
      PaperProps={{
        elevation: 3,
        sx: {
          overflow: 'visible',
          borderRadius: 2,
          mt: 1.5,
          '& .MuiAvatar-root': {
            width: 32,
            height: 32,
            ml: -0.5,
            mr: 1,
          },
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
          },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <MenuItem onClick={() => { handleMenuClose(); navigate('/profile'); }}>
        <ListItemIcon>
          <PersonIcon fontSize="small" color="primary" />
        </ListItemIcon>
        {t.profile}
      </MenuItem>
      <MenuItem onClick={() => { handleMenuClose(); navigate('/create-party'); }}>
        <ListItemIcon>
          <AddIcon fontSize="small" color="primary" />
        </ListItemIcon>
        {t.createParty}
      </MenuItem>
      <MenuItem onClick={() => { handleMenuClose(); navigate('/friends'); }}>
        <ListItemIcon>
          <PeopleIcon fontSize="small" color="primary" />
        </ListItemIcon>
        {t.friends}
      </MenuItem>
      <Divider />
      <MenuItem onClick={handleLogout}>
        <ListItemIcon>
          <LogoutIcon fontSize="small" color="error" />
        </ListItemIcon>
        <Typography color="error">{t.logout}</Typography>
      </MenuItem>
    </Menu>
  );

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', height: '100%', py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
        <Box 
          component="img"
          src={logoSvg}
          alt="GatherUp Logo"
          sx={{ 
            width: 40, 
            height: 40, 
            mr: 1 
          }}
        />
        <Typography variant="h6" color="primary.main" fontWeight="bold">
          {t.appName}
        </Typography>
      </Box>
      
      {user && (
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Avatar 
            src={avatarUrl}
            sx={{ 
              width: 60, 
              height: 60, 
              mb: 1,
              bgcolor: 'primary.main',
              color: 'primary.contrastText'
            }}
          >
            {displayName.charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="subtitle1" fontWeight="medium">
            {displayName}
          </Typography>
        </Box>
      )}
      
      <Divider />
      <List sx={{ py: 2, px: 2 }}>
        <ListItem 
          component={Link} 
          to="/"
          disableGutters
          sx={{ 
            borderRadius: 2, 
            mb: 1,
            pl: 2,
            bgcolor: isActive('/') ? 'primary.light' : 'transparent',
            color: isActive('/') ? 'primary.contrastText' : 'inherit',
            '&:hover': {
              bgcolor: isActive('/') ? 'primary.light' : 'action.hover',
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <HomeIcon color={isActive('/') ? 'inherit' : 'primary'} />
          </ListItemIcon>
          <ListItemText primary={t.home} />
        </ListItem>
        {user ? (
          <>
            <ListItem 
              component={Link} 
              to="/create-party"
              disableGutters
              sx={{ 
                borderRadius: 2, 
                mb: 1,
                pl: 2,
                bgcolor: isActive('/create-party') ? 'primary.light' : 'transparent',
                color: isActive('/create-party') ? 'primary.contrastText' : 'inherit',
                '&:hover': {
                  bgcolor: isActive('/create-party') ? 'primary.light' : 'action.hover',
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <AddIcon color={isActive('/create-party') ? 'inherit' : 'primary'} />
              </ListItemIcon>
              <ListItemText primary={t.createParty} />
            </ListItem>
            
            <ListItem 
              component={Link} 
              to="/friends"
              disableGutters
              sx={{ 
                borderRadius: 2, 
                mb: 1,
                pl: 2,
                bgcolor: isActive('/friends') ? 'primary.light' : 'transparent',
                color: isActive('/friends') ? 'primary.contrastText' : 'inherit',
                '&:hover': {
                  bgcolor: isActive('/friends') ? 'primary.light' : 'action.hover',
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <PeopleIcon color={isActive('/friends') ? 'inherit' : 'primary'} />
              </ListItemIcon>
              <ListItemText primary={t.friends} />
            </ListItem>
            <ListItem 
              component={Link} 
              to="/profile"
              disableGutters
              sx={{ 
                borderRadius: 2, 
                mb: 1,
                pl: 2,
                bgcolor: isActive('/profile') ? 'primary.light' : 'transparent',
                color: isActive('/profile') ? 'primary.contrastText' : 'inherit',
                '&:hover': {
                  bgcolor: isActive('/profile') ? 'primary.light' : 'action.hover',
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <PersonIcon color={isActive('/profile') ? 'inherit' : 'primary'} />
              </ListItemIcon>
              <ListItemText primary={t.profile} />
            </ListItem>
            <Divider sx={{ my: 2 }} />
            
            <ListItem 
              onClick={handleLogout}
              disableGutters
              sx={{ 
                borderRadius: 2,
                pl: 2,
                color: 'error.main',
                '&:hover': {
                  bgcolor: 'error.light',
                  color: 'error.contrastText',
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LogoutIcon color="error" />
              </ListItemIcon>
              <ListItemText primary={t.logout} />
            </ListItem>
          </>
        ) : (
          <>
            <ListItem 
              component={Link} 
              to="/login"
              disableGutters
              sx={{ 
                borderRadius: 2, 
                mb: 1,
                pl: 2,
                bgcolor: isActive('/login') ? 'primary.light' : 'transparent',
                color: isActive('/login') ? 'primary.contrastText' : 'inherit',
                '&:hover': {
                  bgcolor: isActive('/login') ? 'primary.light' : 'action.hover',
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LoginIcon color={isActive('/login') ? 'inherit' : 'primary'} />
              </ListItemIcon>
              <ListItemText primary={t.login} />
            </ListItem>
            
            <ListItem 
              component={Link} 
              to="/signup"
              disableGutters
              sx={{ 
                borderRadius: 2,
                pl: 2,
                bgcolor: isActive('/signup') ? 'primary.light' : 'transparent',
                color: isActive('/signup') ? 'primary.contrastText' : 'inherit',
                '&:hover': {
                  bgcolor: isActive('/signup') ? 'primary.light' : 'action.hover',
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <PersonIcon color={isActive('/signup') ? 'inherit' : 'primary'} />
              </ListItemIcon>
              <ListItemText primary={t.signup} />
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <AppBar 
      position="sticky" 
      elevation={0}
      sx={{ 
        bgcolor: 'background.paper', 
        borderBottom: 1, 
        borderColor: 'divider',
        color: 'text.primary'
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters>
          {isMobile ? (
            <>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, justifyContent: 'center' }}>
                <Box 
                  component="img"
                  src={logoSvg}
                  alt="GatherUp Logo"
                  sx={{ 
                    width: 40, 
                    height: 40, 
                    mr: 1 
                  }}
                />
                <Typography
                  variant="h6"
                  noWrap
                  component={Link}
                  to="/"
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    textDecoration: 'none',
                  }}
                >
                  {t.appName}
                </Typography>
              </Box>
              {user && <NotificationBell />}
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
                <Box 
                  component="img"
                  src={logoSvg}
                  alt="GatherUp Logo"
                  sx={{ 
                    width: 40, 
                    height: 40, 
                    mr: 1 
                  }}
                />
                <Typography
                  variant="h6"
                  noWrap
                  component={Link}
                  to="/"
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    textDecoration: 'none',
                  }}
                >
                  {t.appName}
                </Typography>
              </Box>
              
              <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
                <Button 
                  component={Link} 
                  to="/"
                  startIcon={<HomeIcon />}
                  sx={{ 
                    color: isActive('/') ? 'primary.main' : 'text.secondary',
                    borderBottom: isActive('/') ? 2 : 0,
                    borderColor: 'primary.main',
                    borderRadius: 0,
                    px: 2
                  }}
                >
                  {t.home}
                </Button>
                
                {user && (
                  <>
                    <Button 
                      component={Link} 
                      to="/create-party"
                      startIcon={<AddIcon />}
                      sx={{ 
                        color: isActive('/create-party') ? 'primary.main' : 'text.secondary',
                        borderBottom: isActive('/create-party') ? 2 : 0,
                        borderColor: 'primary.main',
                        borderRadius: 0,
                        px: 2
                      }}
                    >
                      {t.createParty}
                    </Button>
                    <Button 
                      component={Link} 
                      to="/friends"
                      startIcon={<PeopleIcon />}
                      sx={{ 
                        color: isActive('/friends') ? 'primary.main' : 'text.secondary',
                        borderBottom: isActive('/friends') ? 2 : 0,
                        borderColor: 'primary.main',
                        borderRadius: 0,
                        px: 2
                      }}
                    >
                      {t.friends}
                    </Button>
                  </>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {user ? (
                  <>
                    <NotificationBell />
                    <IconButton
                      edge="end"
                      aria-label="account of current user"
                      aria-controls={menuId}
                      aria-haspopup="true"
                      onClick={handleProfileMenuOpen}
                      sx={{ 
                        ml: 1,
                        bgcolor: 'primary.light',
                        color: 'primary.contrastText',
                        '&:hover': {
                          bgcolor: 'primary.main',
                        }
                      }}
                    >
                      <Avatar 
                        sx={{ width: 32, height: 32 }}
                        alt={displayName}
                        src={avatarUrl}
                      >
                        {displayName.charAt(0).toUpperCase()}
                      </Avatar>
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Button 
                      variant="outlined" 
                      color="primary" 
                      component={Link} 
                      to="/login"
                      startIcon={<LoginIcon />}
                    >
                      {t.login}
                    </Button>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      component={Link} 
                      to="/signup"
                    >
                      {t.signup}
                    </Button>
                  </>
                )}
              </Box>
            </>
          )}
        </Toolbar>
      </Container>
      
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
            borderRadius: '0 16px 16px 0',
            px: 1
          },
        }}
      >
        {drawer}
      </Drawer>
      
      {renderMenu}
    </AppBar>
  );
};

export default Navbar; 